// Liste du sélecteur de modèles (ModelPicker.tsx) : recherche, regroupements et tris. Sans React,
// vérifiée par scripts/check-vram.ts. Jamais de tri par benchmark : le score ECI d'Epoch AI n'existe
// que pour un modèle sur deux, presque tous gros et connus ; trier par lui enverrait en bas les
// sorties récentes que les visiteurs viennent chercher (étude d'octobre 2026).

import type { ModelPreset } from "./llmVram.ts";

export type PickerMode = "type" | "size" | "new" | "popular";
/** Une ligne : le modèle, son libellé affiché, son labo, ses téléchargements Hugging Face (30 jours). */
export type PickerRow = { m: ModelPreset; label: string; lab?: string; downloads: number };
export type PickerGroup = { title: string; hint?: string; rows: PickerRow[] };

/** Tailles qui parlent de matériel : c'est la taille totale qui occupe la mémoire, MoE compris. */
export const SIZE_BANDS = [
  { max: 10, title: "Up to 10B", hint: "a laptop GPU or a Mac" },
  { max: 40, title: "10B to 40B", hint: "one gaming GPU at 4-bit" },
  { max: 200, title: "40B to 200B", hint: "a workstation or 2 to 4 GPUs" },
  { max: Infinity, title: "Over 200B", hint: "a GPU server" },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const monthTitle = (day: string) => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;

/** « 4.7B », « 28B », « 2780B » : la taille comme les noms des labos l'écrivent. */
export const sizeLabel = (b: number) => (b < 10 ? `${Number(b.toFixed(1))}B` : `${Math.round(b)}B`);

/** Chaque mot de la recherche dans le nom, le labo, le dépôt, la catégorie ou la taille (« qwen 27 »). */
export function matchesQuery(r: PickerRow, query: string): boolean {
  const hay = `${r.label} ${r.lab ?? ""} ${r.m.repo ?? ""} ${r.m.group} ${sizeLabel(r.m.params)}`.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

// À taille égale, le plus récent d'abord : les versions d'une famille se suivent (Qwen3.5 4B, 9B, 27B).
const bySize = (a: PickerRow, b: PickerRow) => a.m.params - b.m.params || (b.m.released ?? "").localeCompare(a.m.released ?? "");

/** Groupes à afficher, vides retirés. type : les catégories du site, dans leur ordre, triées par taille ;
 *  size : par taille ; new : par mois de sortie, le plus récent d'abord ; popular : par téléchargements. */
export function pickerGroups(rows: PickerRow[], mode: PickerMode, groupOrder: string[]): PickerGroup[] {
  let groups: PickerGroup[];
  if (mode === "type") {
    groups = groupOrder.map((g) => ({ title: g, rows: rows.filter((r) => r.m.group === g).sort(bySize) }));
  } else if (mode === "size") {
    groups = SIZE_BANDS.map((b, i) => ({
      title: b.title, hint: b.hint,
      rows: rows.filter((r) => r.m.params < b.max && r.m.params >= (i ? SIZE_BANDS[i - 1].max : 0)).sort(bySize),
    }));
  } else if (mode === "new") {
    groups = [];
    const dated = rows.filter((r) => r.m.released).sort((a, b) => b.m.released!.localeCompare(a.m.released!));
    for (const r of dated) {
      const title = monthTitle(r.m.released!);
      if (groups.at(-1)?.title !== title) groups.push({ title, rows: [] });
      groups.at(-1)!.rows.push(r);
    }
    const undated = rows.filter((r) => !r.m.released);
    if (undated.length) groups.push({ title: "Release date unknown", rows: undated.sort(bySize) });
  } else {
    groups = [{ title: "Most downloaded", hint: "on Hugging Face, last 30 days", rows: [...rows].sort((a, b) => b.downloads - a.downloads || bySize(a, b)) }];
  }
  return groups.filter((g) => g.rows.length);
}
